import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from '../../../../services/auth.service';
import { BackOfficeService } from '../../../../services/backoffice.service';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { MyConfigOrganization } from '../../../../models/myConfig';
import { Order, OrganizationParkingsVM } from '../../../..//models/orderModel';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { MyConfigService } from '../../../../services/my-config.service';
import { BackOffice24StatesService } from '../state/back-office24-states.service';


@Component({
  selector: 'app-back-office24-parkingcheckinout',
  templateUrl: './back-office24-parkingcheckinout.component.html',
  styleUrl: './back-office24-parkingcheckinout.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BackOffice24ParkingCheckInOutComponent implements OnInit, OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject();
  isLoading: boolean = false;
  activeRequests: number = 0;
  @Input() organization!: MyConfigOrganization;
  private orgSubscription: Subscription = new Subscription();
  organizationParkings!: OrganizationParkingsVM;
  dataSourceCheckIn = new MatTableDataSource<any>([]);
  dataSourceCheckOut = new MatTableDataSource<any>([]);
  order!: Order;
  selectedFilter: string = 'all';
  selectedParkingFilter: string = 'all';
  parkingFilterOptions: Array<{ value: string; label: string }> = [];

  lastReloadData = new Date();
  intervalIdData: any;

  lastReloadkavstaBatch = new Date();
  intervalIdkavstaBatch: any;

  displayedColumnsCheckIn: string[] = ['orderNr', 'parkingFrom', 'carId', 'parkingSiteName'];
  displayedColumnsCheckOut: string[] = ['orderNr', 'parkingTo', 'carId', 'parkingSiteName'];
  emptyDataCheckIn = new MatTableDataSource([{ empty: "row" }]);
  emptyDataCheckOut = new MatTableDataSource([{ empty: "row" }]);

  @ViewChild('sortCheckIn', { static: false }) sortCheckIn!: MatSort;
  @ViewChild('sortCheckOut', { static: false }) sortCheckOut!: MatSort;

  constructor(
    public authService: AuthService,
    private BackOfficeService: BackOfficeService,
    public router: Router,
    private route: ActivatedRoute,
    private myConfigService: MyConfigService,
    public backOffice24StatesService: BackOffice24StatesService,
  ) { }

  ngOnInit(): void {
    try {
      this.orgSubscription = this.backOffice24StatesService.currentOrganization$.subscribe(org => {
        this.organization = org ?? this.organization;
        if (this.organization?.organizationId) {
          this.getOrganizationOrderParkings();
          this.loadParkingFilterOptions();
        }
      });
      this.intervalIdData = setInterval(() => this.getOrganizationOrderParkings(), 60000);

    } catch (e) {
      console.log(e);
    }
  }

  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  incrementActiveRequests() {
    this.activeRequests++;
    if (this.activeRequests === 1) {
      this.setIsLoading(true);
    }
  }

  decrementActiveRequests() {
    if (this.activeRequests > 0) {
      this.activeRequests--;
    }
    if (this.activeRequests === 0) {
      this.setIsLoading(false);
    }
  }

  ngAfterViewInit() {
    this.dataSourceCheckIn.sort = this.sortCheckIn;
    this.dataSourceCheckOut.sort = this.sortCheckOut;
  }

  loadParkingFilterOptions() {
    if (!this.organization?.organizationId) {
      return;
    }

    this.BackOfficeService
      .getOrganizationParkings(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (parkings: any[]) => {
          const uniqueParkings = new Map<string, string>();
          parkings?.forEach((parking: any) => {
            const parkingName = parking?.parkingName || parking?.name || '';
            if (parkingName && !uniqueParkings.has(parkingName.toLowerCase())) {
              uniqueParkings.set(parkingName.toLowerCase(), parkingName);
            }
          });

          this.parkingFilterOptions = Array.from(uniqueParkings.values())
            .sort((a, b) => a.localeCompare(b))
            .map(name => ({ value: name, label: name }));
        },
        error: (error) => console.error(error)
      });
  }

  getOrganizationOrderParkings() {
    if (!this.organization?.organizationId) {
      return;
    }

    this.incrementActiveRequests();
    this.BackOfficeService
      .getOrganizationOrderParkings(this.organization.organizationId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (parkingVMs) => {
          this.organizationParkings = parkingVMs;
          this.decrementActiveRequests();

          const currentTime = new Date();
          const swedenTime = new Date(currentTime.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));

          parkingVMs.parkingsPaidNoArrived.forEach(parking => {
            const parkingFromTime = new Date(parking.parkingFrom);
            if (parkingFromTime <= swedenTime) {
              parking.status = "Delayed";
            }
          });

          parkingVMs.parkingsForCheckOut.forEach(parking => {
            const parkingToTime = new Date(parking.parkingTo);
            if (swedenTime >= parkingToTime) {
              parking.status = "Delayed";
            }
          });
          this.dataSourceCheckIn.data = this.organizationParkings.parkingsPaidNoArrived.sort((a, b) =>
            new Date(a.parkingFrom).getTime() - new Date(b.parkingFrom).getTime()
          );

          this.dataSourceCheckOut.data = this.organizationParkings.parkingsForCheckOut.sort((a, b) =>
            new Date(a.parkingTo).getTime() - new Date(b.parkingTo).getTime()
          );


          this.lastReloadData = new Date();

          setTimeout(() => {
            this.dataSourceCheckIn.sort = this.sortCheckIn;
            this.dataSourceCheckOut.sort = this.sortCheckOut;
          });

        },
        error: (error) => {
          this.decrementActiveRequests();
          console.error(error);
        }
      });
  }

  goToOrder(order: Order): void {
    this.order = order;
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${order.orderId}`]));
    window.open(url, '_blank');
  }

  private normalizeValue(value: string | undefined | null): string {
    return (value || '').trim().toLowerCase();
  }

  applyFilter() {
    const currentTime = new Date();
    const swedenTime = new Date(currentTime.toLocaleString("en-US", { timeZone: "Europe/Stockholm" }));

    this.dataSourceCheckIn.data = this.organizationParkings.parkingsPaidNoArrived.filter(parking => {
      const matchesStatus = this.selectedFilter === 'delayed'
        ? parking.status === "Delayed"
        : this.selectedFilter === 'upcoming'
          ? new Date(parking.parkingFrom) >= swedenTime
          : true;

      const matchesParking = this.selectedParkingFilter === 'all'
        ? true
        : this.normalizeValue(parking.parking) === this.normalizeValue(this.selectedParkingFilter);

      return matchesStatus && matchesParking;
    });

    this.dataSourceCheckOut.data = this.organizationParkings.parkingsForCheckOut.filter(parking => {
      const matchesStatus = this.selectedFilter === 'delayed'
        ? parking.status === "Delayed"
        : this.selectedFilter === 'upcoming'
          ? new Date(parking.parkingTo) >= swedenTime
          : true;

      const matchesParking = this.selectedParkingFilter === 'all'
        ? true
        : this.normalizeValue(parking.parking) === this.normalizeValue(this.selectedParkingFilter);

      return matchesStatus && matchesParking;
    });
  }

  ngOnDestroy(): void {
    // throw new Error('Method not implemented.');
    this.orgSubscription.unsubscribe();
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

}
